import React, { useState } from 'react';
import { oneOf } from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import classNames from 'classnames';

import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { FormattedMessage, useIntl } from '../../util/reactIntl';
import { ensureCurrentUser } from '../../util/data';
import { isTooManyEmailVerificationRequestsError } from '../../util/errors';
import { isEmailVerificationPromptExcluded } from '../../util/emailVerificationPrompt';
import {
  dismissEmailVerificationPrompt,
  isEmailVerificationPromptDismissed,
} from '../../util/emailVerificationDismissState';
import { sendVerificationEmail } from '../../ducks/user.duck';
import { IconEmailAttention, NamedLink } from '../../components';

import css from './EmailVerificationWidget.module.css';

// Non-blocking replacement for the old ModalMissingInformation/EmailReminder overlay.
// Desktop: a small card anchored under the profile avatar, the same "floating widget
// near an icon" idea as the cart mini-widget. Mobile: an in-flow banner under the
// topbar instead - a floating card there kept landing on top of per-page sticky
// elements (the booking footer's "Select Dates" CTA, the Intercom launcher) that we
// can't fully enumerate or avoid via positioning alone. Living in the normal document
// flow at the top of the page sidesteps that entirely.
const EmailVerificationWidget = ({ placement }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  const [dismissed, setDismissed] = useState(isEmailVerificationPromptDismissed);

  const { currentUser, sendVerificationEmailInProgress, sendVerificationEmailError } = useSelector(
    state => state.user
  );
  const { isAuthenticated } = useSelector(state => state.auth);

  const user = ensureCurrentUser(currentUser);
  const isUnverified = isAuthenticated && !!user.id && user.attributes.emailVerified === false;
  const isExcludedRoute = isEmailVerificationPromptExcluded(
    location.pathname,
    routeConfiguration
  );

  if (!isUnverified || isExcludedRoute || dismissed) {
    return null;
  }

  const handleClose = () => {
    dismissEmailVerificationPrompt();
    setDismissed(true);
  };

  const handleResend = () => {
    dispatch(sendVerificationEmail());
  };

  const email = <span className={css.email}>{user.attributes.email}</span>;
  const resendErrorTranslationId = isTooManyEmailVerificationRequestsError(
    sendVerificationEmailError
  )
    ? 'EmailVerificationWidget.resendFailedTooManyRequests'
    : 'EmailVerificationWidget.resendFailed';

  const resendEmailLink = (
    <button type="button" className={css.helperLink} onClick={handleResend}>
      <FormattedMessage id="EmailVerificationWidget.resendEmailLinkText" />
    </button>
  );

  const fixEmailLink = (
    <NamedLink className={css.helperLink} name="ContactDetailsPage">
      <FormattedMessage id="EmailVerificationWidget.fixEmailLinkText" />
    </NamedLink>
  );

  const closeButtonLabel = intl.formatMessage({ id: 'EmailVerificationWidget.dismiss' });

  const closeButton = (
    <button
      type="button"
      className={css.closeButton}
      onClick={handleClose}
      aria-label={closeButtonLabel}
    >
      <i className="ri-close-line" />
    </button>
  );

  const mobileBannerCloseButton = (
    <button
      type="button"
      className={css.mobileBannerClose}
      onClick={handleClose}
      aria-label={closeButtonLabel}
    >
      <i className="ri-close-line" />
    </button>
  );

  const errorMessage = sendVerificationEmailError ? (
    <p className={css.error}>
      <FormattedMessage id={resendErrorTranslationId} />
    </p>
  ) : null;

  const resendHelperText = sendVerificationEmailInProgress ? (
    <FormattedMessage id="EmailVerificationWidget.sendingEmail" />
  ) : (
    <FormattedMessage id="EmailVerificationWidget.resendEmail" values={{ resendEmailLink }} />
  );

  if (placement === 'mobileBanner') {
    return (
      <div className={css.mobileBanner} role="status" aria-live="polite">
        <div className={css.mobileBannerRow}>
          <IconEmailAttention className={css.mobileBannerIcon} />
          <p className={css.mobileBannerMessage}>
            <FormattedMessage id="EmailVerificationWidget.messageCompact" values={{ email }} />
          </p>
          {mobileBannerCloseButton}
        </div>
        {errorMessage}
        <div className={css.mobileBannerActions}>
          <p className={css.helperText}>{resendHelperText}</p>
          <p className={css.helperText}>
            <FormattedMessage id="EmailVerificationWidget.fixEmail" values={{ fixEmailLink }} />
          </p>
        </div>
      </div>
    );
  }

  return (
    <div
      className={classNames(css.root, css[placement])}
      role="status"
      aria-live="polite"
    >
      {closeButton}

      <IconEmailAttention className={css.icon} />

      <p className={css.message}>
        <FormattedMessage id="EmailVerificationWidget.messageCompact" values={{ email }} />
      </p>

      {errorMessage}

      <div className={css.actions}>
        <p className={css.helperText}>{resendHelperText}</p>
        <p className={css.helperText}>
          <FormattedMessage id="EmailVerificationWidget.fixEmail" values={{ fixEmailLink }} />
        </p>
      </div>
    </div>
  );
};

EmailVerificationWidget.defaultProps = {
  placement: 'desktop',
};

EmailVerificationWidget.propTypes = {
  placement: oneOf(['desktop', 'mobileBanner']),
};

export default EmailVerificationWidget;
